import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { api } from '../api/endpoints';
import { useMe } from '../auth/context';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageHeader } from '../components/PageHeader';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { BrokerProfileSection } from '../documents/BrokerProfileSection';
import { NotificationSettings, PasswordChange, RouteStartSettings } from './ProfileSettings';

const outlookMessages: Record<string, { tone: string; text: string }> = {
  connected: { tone: 'success', text: 'Outlook wurde verbunden. Termine werden ab jetzt automatisch eingetragen.' },
  failed: { tone: 'error', text: 'Die Verbindung mit Outlook ist fehlgeschlagen. Bitte erneut versuchen.' },
  declined: { tone: 'info', text: 'Die Anmeldung bei Microsoft wurde abgebrochen.' },
  tenant: { tone: 'error', text: 'Dieses Microsoft-Konto gehört zu keinem freigegebenen Unternehmen. Bitte mit dem Firmenkonto anmelden.' },
};

// ProfilePage: persönliche Einstellungen – Telefonie (Browser oder eigenes Telefon) und Outlook-Verbindung.
export function ProfilePage() {
  const me = useMe();
  const { project, can } = useProject();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const agent = useQuery({ queryKey: ['me', 'telephony'], queryFn: ({ signal }) => api.telephonyAgent(signal) });
  const outlook = useQuery({ queryKey: ['me', 'outlook'], queryFn: ({ signal }) => api.outlookStatus(signal) });
  const [phone, setPhone] = useState<string>();
  const savePhone = useMutation({
    mutationFn: (v: string) => api.saveTelephonyAgent({ endpoint_type: 'PHONE', phone_number: v }),
    onSuccess: (data) => queryClient.setQueryData(['me', 'telephony'], data),
  });
  const chooseBrowser = useMutation({
    mutationFn: () => api.saveTelephonyAgent({ endpoint_type: 'CLIENT' }),
    onSuccess: (data) => queryClient.setQueryData(['me', 'telephony'], data),
  });
  const connect = useMutation({
    mutationFn: () => api.connectOutlook(`/p/${project.key}/profil`),
    onSuccess: ({ authorization_url }) => window.location.assign(authorization_url),
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectOutlook(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me', 'outlook'] }),
  });
  const message = outlookMessages[params.get('outlook') ?? ''];
  const conn = outlook.data?.connection;
  const value = phone ?? agent.data?.agent.phone_number ?? '';

  return (
    <div className="page">
      <PageHeader title="Mein Profil" description="Passwort, Benachrichtigungen, Kalender und Telefon." />
      <section className="card">
        <h2>
          {me.user.first_name} {me.user.last_name}
        </h2>
        <p className="muted">{me.user.email}</p>
      </section>
      <section className="card" aria-labelledby="telephony-title">
        <h2 id="telephony-title">Telefonie</h2>
        {agent.isPending ? (
          <PageLoader />
        ) : agent.isError ? (
          <ErrorMessage error={agent.error} />
        ) : (
          <>
            <p className="muted">
              {agent.data.mode === 'BROWSER'
                ? 'Sie telefonieren im Browser (Headset). Kunden sehen immer die feste Firmennummer.'
                : 'Beim Klick auf „Anruf starten“ ruft das System zuerst Ihre Nummer an und verbindet Sie dann mit dem Kunden. Kunden sehen immer die feste Firmennummer.'}
            </p>
            {agent.data.browser_available && (
              <div className="inline-form">
                <button
                  type="button"
                  className={agent.data.mode === 'BROWSER' ? 'btn btn-primary' : 'btn'}
                  aria-pressed={agent.data.mode === 'BROWSER'}
                  disabled={chooseBrowser.isPending || agent.data.mode === 'BROWSER'}
                  onClick={() => chooseBrowser.mutate()}
                >
                  Im Browser telefonieren
                </button>
                <span className="muted">oder auf eigenem Telefon:</span>
              </div>
            )}
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                savePhone.mutate(value);
              }}
            >
              <label className="field">
                <span>Meine Telefonnummer</span>
                <input type="tel" value={value} onChange={(e) => setPhone(e.target.value)} placeholder="+49 711 123456" required />
              </label>
              <button type="submit" className={agent.data.mode === 'PHONE' ? 'btn btn-primary' : 'btn'} disabled={savePhone.isPending}>
                {agent.data.browser_available ? 'Auf dieses Telefon' : 'Speichern'}
              </button>
              {(savePhone.isSuccess || chooseBrowser.isSuccess) && <span className="muted">Gespeichert</span>}
              <span className="muted">Anbieter: {agent.data.provider || 'nicht konfiguriert'}</span>
            </form>
          </>
        )}
        {(savePhone.isError || chooseBrowser.isError) && <MutationError error={savePhone.error ?? chooseBrowser.error} />}
      </section>
      <section className="card" aria-labelledby="outlook-title">
        <h2 id="outlook-title">Outlook-Kalender</h2>
        {message && <p className={`alert alert-${message.tone}`}>{message.text}</p>}
        {outlook.isPending ? (
          <PageLoader />
        ) : outlook.isError ? (
          <ErrorMessage error={outlook.error} />
        ) : !outlook.data.available ? (
          <p className="muted">Die Outlook-Integration ist in dieser Umgebung nicht eingerichtet.</p>
        ) : conn && conn.status !== 'DISCONNECTED' ? (
          <>
            <dl className="facts">
              <dt>Konto</dt>
              <dd>{conn.account_email ?? '–'}</dd>
              <dt>Kalender</dt>
              <dd>{conn.calendar_name ?? '–'}</dd>
              <dt>Status</dt>
              <dd>
                {conn.status === 'ACTIVE' ? 'Verbunden' : conn.status === 'REAUTH_REQUIRED' ? 'Bitte neu anmelden' : conn.status}
                {conn.last_error && <span className="muted"> · {conn.last_error}</span>}
              </dd>
              <dt>Letzter Abgleich</dt>
              <dd>{fmtDateTime(conn.last_sync_at)}</dd>
            </dl>
            <div className="form-actions">
              {conn.status !== 'ACTIVE' && (
                <button type="button" className="btn btn-primary" disabled={connect.isPending} onClick={() => connect.mutate()}>
                  Neu verbinden
                </button>
              )}
              <button type="button" className="btn" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
                Verbindung trennen
              </button>
            </div>
          </>
        ) : outlook.data.can_connect ? (
          <>
            <p className="muted">Termine aus Maklerpass werden automatisch in Ihren Outlook-Kalender eingetragen und aktualisiert.</p>
            <button type="button" className="btn btn-primary" disabled={connect.isPending} onClick={() => connect.mutate()}>
              Mit Outlook verbinden
            </button>
          </>
        ) : (
          <p className="muted">Für Ihre Rolle ist keine Outlook-Verbindung vorgesehen.</p>
        )}
        {(connect.isError || disconnect.isError) && <MutationError error={connect.error ?? disconnect.error} />}
      </section>
      <NotificationSettings />
      {can('appointment.read') && project.roles.some((r) => r.key === 'FIELD_SALES') && <RouteStartSettings />}
      {can('document.edit') && <BrokerProfileSection />}
      <PasswordChange />
    </div>
  );
}
